import { doorRules, getBrand } from '@planner/config';
import { isValidEmail, type Order } from '@planner/shared';
import { cartDeclarations, doorCartTotals, parseDoorsDesign } from '@planner/shared/doors';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import bunningsArtwork from '../assets/doors/bunnings-available.webp';
import { CartEmailDialog } from '../components/CartEmailDialog.js';
import { CartLineCard } from '../components/CartLineCard.js';
import { CartIcon } from '../components/CartIcon.js';
import { StepProgress } from '../components/StepProgress.js';
import {
  confirmCartDeclarations,
  downloadCartDocument,
  emailCart,
  fetchCart,
  saveDoorCart,
} from '../lib/api.js';
import { inspectCart, type CartLine } from '../lib/cart.js';
import { brand, region } from '../lib/env.js';
import { formatPrice } from '../lib/format.js';
import { useDoorStore } from '../stores/doorStore.js';

const actionClass =
  'flex min-h-12 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none';

export function CartStep() {
  const reference = useDoorStore((state) => state.cartReference);
  const setConfirmedCart = useDoorStore((state) => state.setConfirmedCart);
  const clearCart = useDoorStore((state) => state.clearCart);
  const editSavedDoor = useDoorStore((state) => state.editSavedDoor);
  const resetDraft = useDoorStore((state) => state.resetDraft);
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [clearWarning, setClearWarning] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [accepted, setAccepted] = useState({ drawing: false, terms: false, privacy: false });
  const [confirmationId, setConfirmationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const request = useRef(0);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const emailOpener = useRef<HTMLButtonElement | null>(null);

  useBlocker({
    shouldBlockFn: () => busyRef.current || sendingRef.current,
    enableBeforeUnload: () => busyRef.current || sendingRef.current,
  });

  const load = useCallback(async () => {
    if (!reference) return;
    const currentRequest = ++request.current;
    setLoading(true);
    setLoadError(null);
    try {
      const saved = await fetchCart(reference);
      if (
        currentRequest === request.current &&
        useDoorStore.getState().cartReference === reference
      ) {
        setOrder(saved);
        setConfirmedCart(saved);
      }
    } catch (error) {
      if (currentRequest === request.current) {
        setLoadError(
          error instanceof Error
            ? error.message
            : 'The saved Cart could not be loaded. Please retry.',
        );
      }
    } finally {
      if (currentRequest === request.current) setLoading(false);
    }
  }, [reference, setConfirmedCart]);

  useEffect(() => {
    setOrder(null);
    setLoadError(null);
    setActionError(null);
    setConfirmClear(false);
    setAccepted({ drawing: false, terms: false, privacy: false });
    setConfirmationId(null);
    setEmailOpen(false);
    setEmail('');
    setEmailError(null);
    setEmailSent(false);
    if (reference) setClearWarning(false);
    if (reference) void load();
    return () => {
      request.current += 1;
    };
  }, [reference, load]);

  const activeOrder = order?.referenceCode === reference ? order : null;
  const cartBrand = activeOrder ? getBrand(activeOrder.brand) : brand;
  const bunnings = activeOrder?.channel.type === 'bunnings';
  const view = activeOrder ? inspectCart(activeOrder) : null;
  const lines = view?.valid ? view.lines : [];
  const outputReady =
    !!activeOrder &&
    view?.valid === true &&
    view.totalsValid &&
    lines.length > 0 &&
    lines.every((line) => line.errors.length === 0) &&
    accepted.drawing &&
    accepted.terms &&
    accepted.privacy &&
    !!cartBrand.legalUrls;
  const readOnly = activeOrder?.status !== 'draft';

  const run = async (action: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : 'The Cart action failed. Please retry.',
      );
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const startNew = async () => {
    resetDraft();
    await navigate({ to: '/planner/type' });
  };
  const edit = async (line: CartLine) => {
    try {
      editSavedDoor(line.door);
      await navigate({ to: '/planner/measure' });
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : 'This door could not be opened for editing.',
      );
    }
  };
  const clear = () => {
    clearCart();
    if (useDoorStore.getState().persistence === 'unavailable') {
      setClearWarning(true);
    }
  };
  const remove = (line: CartLine) =>
    void run(async () => {
      if (!activeOrder || !view?.valid) return;
      if (view.lines.length === 1) {
        clear();
        return;
      }
      const design = parseDoorsDesign(activeOrder.design);
      if (!design) throw new Error('The saved Cart could not be matched to its doors.');
      const remaining = view.lines.filter((item) => item.door.lineId !== line.door.lineId);
      const items = remaining.map((item) => item.item);
      const saved = await saveDoorCart({
        region: activeOrder.region,
        brand: activeOrder.brand,
        design: { ...design, doors: remaining.map((item) => item.door) },
        items,
        totals: doorCartTotals(items, region),
        baseReference: activeOrder.referenceCode,
        removingLineId: line.door.lineId,
      });
      if (saved.referenceCode === activeOrder.referenceCode) {
        throw new Error('The save could not be confirmed. Your previous Cart has been kept.');
      }
      setConfirmedCart(saved);
    });
  const confirmation = async () => {
    if (!activeOrder || !outputReady)
      throw new Error('Confirm all declarations before continuing.');
    if (confirmationId) return confirmationId;
    const id = await confirmCartDeclarations(activeOrder.referenceCode);
    setConfirmationId(id);
    return id;
  };
  const download = (lineId?: string) =>
    void run(async () => {
      if (!activeOrder) return;
      const id = await confirmation();
      const pdf = await downloadCartDocument(activeOrder.referenceCode, id, lineId);
      const url = URL.createObjectURL(pdf);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = `${activeOrder.referenceCode}-${lineId ? 'drawing' : 'cart-summary'}.pdf`;
        link.click();
      } finally {
        URL.revokeObjectURL(url);
      }
    });
  const openEmail = (event: MouseEvent<HTMLButtonElement>) => {
    emailOpener.current = event.currentTarget;
    void run(async () => {
      await confirmation();
      setEmailError(null);
      setEmailSent(false);
      setEmailOpen(true);
    });
  };
  const send = async () => {
    if (sendingRef.current || !activeOrder || !confirmationId) return;
    if (!isValidEmail(email)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setEmailError(null);
    setEmailSent(false);
    try {
      await emailCart(activeOrder.referenceCode, confirmationId, email.trim());
      setEmail(email.trim());
      setEmailSent(true);
    } catch (error) {
      setEmailError(
        error instanceof Error ? error.message : 'The email could not be sent. Please retry.',
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const actions = (mobile: boolean) => (
    <div
      className={
        mobile
          ? 'fixed inset-x-0 bottom-0 z-40 flex gap-3 border-t border-gray-200 bg-white/95 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur-sm sm:hidden'
          : 'mb-6 hidden justify-end gap-3 sm:flex'
      }
    >
      <button
        type="button"
        data-name={mobile ? 'cart-download-summary-mobile' : 'cart-download-summary'}
        disabled={!outputReady || busy}
        onClick={() => download()}
        className={`${actionClass} flex-[2] bg-gray-700 hover:bg-gray-800 sm:flex-none`}
      >
        <CartIcon name="download" /> Download cart summary
      </button>
      <button
        type="button"
        data-name={mobile ? 'cart-email-mobile' : 'cart-email'}
        aria-label="Email cart"
        disabled={!outputReady || busy}
        onClick={openEmail}
        className={`${actionClass} flex-1 bg-progress shadow-md shadow-progress/20 hover:opacity-90 sm:flex-none`}
      >
        <CartIcon name="mail" /> {mobile ? 'Email' : 'Email cart'}
      </button>
    </div>
  );

  return (
    <>
      <StepProgress currentIndex={5} disabled={busy || sending} />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 pb-32 sm:py-10">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800 sm:text-3xl">Your cart</h1>
            <p className="mt-1 text-sm text-gray-500">
              {reference
                ? 'Print / Download or email your cart summary'
                : 'Design a door to get started.'}
            </p>
            {activeOrder && (
              <div
                data-name="cart-saved-reference"
                className="mt-3 inline-flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 py-2"
              >
                <span className="text-xs font-medium text-gray-500">Design reference</span>
                <span className="font-mono text-sm font-bold tracking-wide text-progress">
                  {activeOrder.referenceCode}
                </span>
              </div>
            )}
          </div>
          {activeOrder && view?.valid && !confirmClear && (
            <div className="flex items-stretch gap-2">
              <button
                type="button"
                data-name="cart-add"
                disabled={busy || readOnly || lines.length >= doorRules.maxDoorsPerOrder}
                onClick={() => void startNew()}
                className="min-h-11 flex-1 rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 hover:text-progress disabled:opacity-40 sm:flex-none"
              >
                <span className="font-bold text-progress">+</span> Add another door
              </button>
              <button
                type="button"
                data-name="cart-clear"
                disabled={busy}
                onClick={() => setConfirmClear(true)}
                className="min-h-11 rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-500 disabled:opacity-40"
              >
                Clear cart
              </button>
            </div>
          )}
        </div>
        {confirmClear && (
          <div className="mb-5 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-page px-4 py-3">
            <span className="mr-1 text-sm text-gray-700">
              {activeOrder
                ? 'Remove all doors from your cart? Previous references and documents will remain valid.'
                : 'Remove this saved reference from your browser? Your draft and previous documents will remain unchanged.'}
            </span>
            <button
              type="button"
              data-name="cart-confirm-clear"
              disabled={busy}
              onClick={clear}
              className="min-h-10 rounded-xl bg-gray-900 px-4 text-sm font-semibold text-white"
            >
              Yes, clear
            </button>
            <button
              type="button"
              data-name="cart-cancel-clear"
              disabled={busy}
              onClick={() => setConfirmClear(false)}
              className="min-h-10 rounded-xl border border-gray-200 bg-white px-4 text-sm text-gray-600"
            >
              Cancel
            </button>
          </div>
        )}
        {actionError && (
          <p role="alert" className="mb-4 text-sm font-medium text-red-700">
            {actionError}
          </p>
        )}
        {clearWarning && (
          <p role="alert" className="mb-4 text-sm font-medium text-red-700">
            The Cart was cleared in this tab, but browser storage is unavailable. It may reappear
            after reload.
          </p>
        )}
        {reference && (loading || (!activeOrder && !loadError)) && (
          <p role="status" className="mb-5 text-sm text-gray-600">
            Loading your saved Cart…
          </p>
        )}
        {reference && loadError && (
          <div
            role="alert"
            className="mb-6 rounded-xl border border-gray-200 bg-white p-5 text-sm text-gray-700"
          >
            <p>{loadError} Your saved reference and draft have been kept.</p>
            <button
              type="button"
              data-name="cart-retry"
              onClick={() => void load()}
              className="mt-3 min-h-10 rounded-xl border border-gray-200 px-4 font-semibold"
            >
              Retry
            </button>
            {!confirmClear && (
              <button
                type="button"
                data-name="cart-remove-unavailable-reference"
                onClick={() => setConfirmClear(true)}
                className="mt-3 ml-2 min-h-10 rounded-xl border border-gray-200 px-4 font-semibold"
              >
                Remove saved reference
              </button>
            )}
          </div>
        )}
        {!reference && (
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-10 text-center">
            <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-orange-50 text-progress">
              <CartIcon name="bag" className="size-6" />
            </div>
            <p className="mb-1 font-semibold text-gray-700">Your cart is empty</p>
            <p className="mb-5 text-xs text-gray-400">
              Design a door and add it to your cart to get started
            </p>
            <button
              type="button"
              data-name="cart-start"
              onClick={() => void startNew()}
              className="rounded-xl bg-progress px-6 py-3 text-sm font-semibold text-white shadow-md shadow-progress/20"
            >
              + Start New Design
            </button>
          </div>
        )}
        {view && !view.valid && (
          <p
            role="alert"
            className="rounded-xl border border-gray-200 bg-white p-5 text-sm text-red-700"
          >
            {view.message} Your saved reference has been kept.
          </p>
        )}
        {activeOrder && view?.valid && (
          <>
            {readOnly && (
              <p className="mb-4 text-sm text-gray-600">This saved Cart is read-only.</p>
            )}
            {lines.length >= doorRules.maxDoorsPerOrder && (
              <p role="status" className="mb-4 text-sm text-gray-600">
                Your Cart has reached the {doorRules.maxDoorsPerOrder}-door limit. Edit or remove a
                door before adding another.
              </p>
            )}
            {lines.map((line) => (
              <CartLineCard
                key={line.door.lineId}
                line={line}
                disabled={busy || readOnly}
                canExport={outputReady && !busy}
                onEdit={() => void edit(line)}
                onRemove={() => remove(line)}
                onDownload={() => download(line.door.lineId)}
              />
            ))}
            <div className="mb-5 flex flex-col gap-2 rounded-2xl bg-gray-900 px-5 py-4 text-white sm:flex-row sm:items-center sm:justify-between sm:py-5">
              <div>
                <p className="text-sm font-semibold">Total estimated price (GST inclusive)</p>
                <p className="mt-1 max-w-md text-[11px] leading-relaxed text-white/60">
                  {bunnings
                    ? 'Bunnings will confirm the final price at the Special Orders Desk in your local store.'
                    : 'The final price and availability must be confirmed before ordering.'}
                </p>
              </div>
              <span className="text-2xl font-bold text-progress sm:text-3xl">
                {view.totalsValid && lines.every((line) => line.errors.length === 0)
                  ? formatPrice(activeOrder.totals.total)
                  : '—'}
              </span>
            </div>
            {!view.totalsValid && (
              <p role="alert" className="mb-4 text-sm text-red-700">
                The saved Cart total does not match its lines. Outputs are unavailable.
              </p>
            )}
            {lines.some((line) => line.errors.length > 0) && (
              <p role="alert" className="mb-4 text-sm text-red-700">
                Resolve the highlighted door issues before downloading or emailing this Cart.
              </p>
            )}
            {!cartBrand.legalUrls && (
              <p role="alert" className="mb-4 text-sm text-red-700">
                Terms and Privacy links are not available for this brand. Outputs are unavailable.
              </p>
            )}
            {(['drawing', 'terms', 'privacy'] as const).map((kind) => (
              <label
                key={kind}
                className={`mb-3 flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-4 text-sm leading-relaxed text-gray-700 ${accepted[kind] ? 'border-progress bg-progress/5' : 'border-gray-200 bg-page'}`}
              >
                <input
                  type="checkbox"
                  data-name={`cart-declaration-${kind}`}
                  checked={accepted[kind]}
                  disabled={busy}
                  onChange={(event) => {
                    setAccepted((current) => ({ ...current, [kind]: event.target.checked }));
                    setConfirmationId(null);
                  }}
                  className="mt-0.5 size-6 shrink-0 accent-progress"
                />
                <span>
                  {kind === 'drawing' ? (
                    cartDeclarations.drawing.text
                  ) : kind === 'terms' ? (
                    <>
                      I agree to the{' '}
                      {cartBrand.legalUrls ? (
                        <a
                          href={cartBrand.legalUrls.terms}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          className="font-semibold text-progress underline"
                        >
                          Terms and Conditions
                        </a>
                      ) : (
                        'Terms and Conditions'
                      )}
                      .
                    </>
                  ) : (
                    <>
                      I have read the{' '}
                      {cartBrand.legalUrls ? (
                        <a
                          href={cartBrand.legalUrls.privacy}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          className="font-semibold text-progress underline"
                        >
                          Privacy Policy
                        </a>
                      ) : (
                        'Privacy Policy'
                      )}
                      .
                    </>
                  )}
                </span>
              </label>
            ))}
            {actions(false)}
            <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
              <p className="mb-4 text-sm font-semibold text-gray-800">What happens next?</p>
              <ol className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                {[
                  {
                    number: '1',
                    icon: 'print' as const,
                    title: 'Print or email this summary',
                    description:
                      'Save a copy of your door design using the Download or Email buttons.',
                  },
                  {
                    number: '2',
                    icon: 'pin' as const,
                    title: bunnings ? 'Visit any Bunnings Warehouse' : `Contact ${cartBrand.name}`,
                    description: bunnings
                      ? 'Ask for the Special Orders Desk and show them your summary.'
                      : 'Share your summary to confirm pricing and next steps.',
                  },
                  {
                    number: '3',
                    icon: 'badge' as const,
                    title: bunnings ? 'They confirm and order' : 'Confirm your order',
                    description: bunnings
                      ? 'Staff check the price and place your custom door order.'
                      : 'Confirm the final price before placing your custom door order.',
                  },
                ].map(({ number, icon, title, description }) => (
                  <li key={number} className="flex gap-3">
                    <span className="relative flex size-10 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-progress">
                      <CartIcon name={icon} className="size-[18px]" />
                      <span className="absolute -top-1.5 -left-1.5 flex size-5 items-center justify-center rounded-full bg-progress text-[10px] font-bold text-white">
                        {number}
                      </span>
                    </span>
                    <div>
                      <p className="mb-0.5 text-[13px] font-semibold text-gray-800">{title}</p>
                      <p className="text-[13px] leading-relaxed text-gray-500">{description}</p>
                    </div>
                  </li>
                ))}
              </ol>
              {bunnings && (
                <div className="mt-5 flex justify-end">
                  <img
                    src={bunningsArtwork}
                    alt="Available at Bunnings Warehouse"
                    className="h-12 w-auto max-w-full object-contain sm:h-14"
                  />
                </div>
              )}
            </div>
            {actions(true)}
          </>
        )}
      </main>
      {emailOpen && (
        <CartEmailDialog
          email={email}
          setEmail={(value) => {
            setEmail(value);
            setEmailError(null);
            setEmailSent(false);
          }}
          sending={sending}
          error={emailError}
          sent={emailSent}
          returnFocus={emailOpener.current}
          onSend={() => void send()}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </>
  );
}
